import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileCTA from '@/components/MobileCTA';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';
import { Leaf, Sun, Droplets, Thermometer, CheckCircle, Clock, Phone, Star, ArrowRight, Download, Calendar, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import SEO from '@/components/SEO';
import ExpandedFAQSection from '@/components/ExpandedFAQSection';
import RelatedMaterialsLinks from '@/components/RelatedMaterialsLinks';
import { grassFaqs } from '@/data/productFaqs';
import grassVarietiesImage from '@/assets/grass-varieties-texas.jpg';
import stAugustinePalmettoImage from '@/assets/st-augustine-palmetto.jpg';
import zoysiaEmeraldImage from '@/assets/zoysia-emerald.jpg';
import bermudaTifwayImage from '@/assets/bermuda-tifway.jpg';
import stAugustineRaleighImage from '@/assets/st-augustine-raleigh.jpg';

interface Product {
  id: string;
  name_en: string;
  name_es: string;
  description_en: string | null;
  description_es: string | null;
  features_en: string[] | null;
  features_es: string[] | null;
  price_book: Array<{
    list_price: number;
    sale_price: number | null;
    unit_label_en: string;
    unit_label_es: string;
  }>;
}

// Static grass varieties data since we may not have database content yet
const grassVarieties = [
  // St. Augustine
  {
    id: 'st-augustine-raleigh',
    name: 'St. Augustine Raleigh',
    nameEs: 'St. Augustine Raleigh',
    price: 0.82,
    image: stAugustineRaleighImage,
    description: 'Cold-hardy St. Augustine variety ideal for North Texas and partial shade',
    descriptionEs: 'Variedad de St. Augustine resistente al frío ideal para el norte de Texas y sombra parcial',
    features: ['Cold Hardy', 'Shade Tolerant', 'Low Maintenance', 'Disease Resistant'],
    featuresEs: ['Resistente al Frío', 'Tolerante a la Sombra', 'Bajo Mantenimiento', 'Resistente a Enfermedades'],
    sunRequirement: '4-6 hours',
    waterNeed: 'Moderate',
    traffic: 'Medium',
    establishment: '2-3 weeks',
    bestFor: ['Residential lawns', 'Shaded areas', 'North Texas']
  },
  {
    id: 'st-augustine-palmetto',
    name: 'St. Augustine Palmetto',
    nameEs: 'St. Augustine Palmetto',
    price: 0.85,
    image: stAugustinePalmettoImage,
    description: 'Premium St. Augustine with excellent heat tolerance and dense growth',
    descriptionEs: 'St. Augustine premium con excelente tolerancia al calor y crecimiento denso',
    features: ['Heat Tolerant', 'Disease Resistant', 'Dense Growth', 'Shade Tolerant'],
    featuresEs: ['Tolerante al Calor', 'Resistente a Enfermedades', 'Crecimiento Denso', 'Tolerante a la Sombra'],
    sunRequirement: '4-8 hours',
    waterNeed: 'Moderate',
    traffic: 'Medium to High',
    establishment: '2-3 weeks',
    bestFor: ['Residential lawns', 'Commercial properties', 'Parks']
  },
  {
    id: 'st-augustine-floratam',
    name: 'St. Augustine Floratam',
    nameEs: 'St. Augustine Floratam',
    price: 0.85,
    image: grassVarietiesImage,
    description: 'Vigorous St. Augustine cultivar with broad blades and strong performance in full sun',
    descriptionEs: 'Cultivar vigoroso de St. Augustine con hojas anchas y alto rendimiento a pleno sol',
    features: ['Full Sun', 'Heat Tolerant', 'Coarse Texture', 'Rapid Establishment'],
    featuresEs: ['Pleno Sol', 'Tolerante al Calor', 'Textura Gruesa', 'Establecimiento Rápido'],
    sunRequirement: '6-8 hours',
    waterNeed: 'Moderate',
    traffic: 'Medium',
    establishment: '2-3 weeks',
    bestFor: ['Full sun yards', 'Warm climates', 'Residential lawns']
  },
  {
    id: 'st-augustine-provista',
    name: 'St. Augustine ProVista',
    nameEs: 'St. Augustine ProVista',
    price: 0.95,
    image: grassVarietiesImage,
    description: 'Modern St. Augustine cultivar selected for uniform growth and color',
    descriptionEs: 'Cultivar moderno de St. Augustine seleccionado por su crecimiento y color uniformes',
    features: ['Uniform Color', 'Shade Tolerant', 'Dense Turf', 'Low Maintenance'],
    featuresEs: ['Color Uniforme', 'Tolerante a la Sombra', 'Césped Denso', 'Bajo Mantenimiento'],
    sunRequirement: '4-8 hours',
    waterNeed: 'Moderate',
    traffic: 'Medium',
    establishment: '2-3 weeks',
    bestFor: ['Front yards', 'Backyards', 'HOA standards']
  },

  // Bermuda
  {
    id: 'bermuda-tiftuf',
    name: 'Bermuda TifTuf',
    nameEs: 'Bermuda TifTuf',
    price: 0.85,
    image: grassVarietiesImage,
    description: 'Highly drought-tolerant Bermuda with excellent traffic recovery',
    descriptionEs: 'Bermuda altamente tolerante a la sequía con excelente recuperación al tráfico',
    features: ['Drought Tolerant', 'Traffic Resistant', 'Quick Recovery', 'Fine Texture'],
    featuresEs: ['Tolerante a la Sequía', 'Resistente al Tráfico', 'Recuperación Rápida', 'Textura Fina'],
    sunRequirement: '8+ hours',
    waterNeed: 'Low to Moderate',
    traffic: 'Very High',
    establishment: '1-2 weeks',
    bestFor: ['Sports fields', 'High-use areas', 'Commercial']
  },
  {
    id: 'bermuda-tifway-419',
    name: 'Bermuda Tifway 419',
    nameEs: 'Bermuda Tifway 419',
    price: 0.75,
    image: bermudaTifwayImage,
    description: 'Hybrid Bermuda with exceptional durability and quick recovery',
    descriptionEs: 'Bermuda híbrida con durabilidad excepcional y recuperación rápida',
    features: ['Fast Growing', 'Heat Tolerant', 'Traffic Resistant', 'Quick Recovery'],
    featuresEs: ['Crecimiento Rápido', 'Tolerante al Calor', 'Resistente al Tráfico', 'Recuperación Rápida'],
    sunRequirement: '8+ hours',
    waterNeed: 'Moderate to High',
    traffic: 'Very High',
    establishment: '1-2 weeks',
    bestFor: ['Sports fields', 'Commercial properties', 'High-use areas']
  },
  {
    id: 'bermuda-celebration',
    name: 'Bermuda Celebration',
    nameEs: 'Bermuda Celebration',
    price: 0.80,
    image: grassVarietiesImage,
    description: 'Dark blue-green Bermuda known for wear tolerance and quick recovery',
    descriptionEs: 'Bermuda verde azulada conocida por su tolerancia al desgaste y recuperación rápida',
    features: ['Wear Resistant', 'Quick Recovery', 'Fine Texture', 'Heat Tolerant'],
    featuresEs: ['Resistente al Desgaste', 'Recuperación Rápida', 'Textura Fina', 'Tolerante al Calor'],
    sunRequirement: '8+ hours',
    waterNeed: 'Moderate',
    traffic: 'High',
    establishment: '1-2 weeks',
    bestFor: ['Athletic fields', 'Parks', 'Residential']
  },
  {
    id: 'bermuda-common',
    name: 'Common Bermuda',
    nameEs: 'Bermuda Común',
    price: 0.65,
    image: grassVarietiesImage,
    description: 'Economical Bermuda variety suitable for full sun and high traffic',
    descriptionEs: 'Variedad económica de Bermuda adecuada para pleno sol y alto tráfico',
    features: ['Cost Effective', 'Heat Tolerant', 'Drought Resistant', 'Low Maintenance'],
    featuresEs: ['Económica', 'Tolerante al Calor', 'Resistente a la Sequía', 'Bajo Mantenimiento'],
    sunRequirement: '8+ hours',
    waterNeed: 'Low to Moderate',
    traffic: 'High',
    establishment: '2-3 weeks',
    bestFor: ['Budget projects', 'Full sun areas', 'Large lawns']
  },

  // Zoysia
  {
    id: 'zoysia-emerald',
    name: 'Zoysia Emerald',
    nameEs: 'Zoysia Esmeralda',
    price: 1.15,
    image: zoysiaEmeraldImage,
    description: 'Fine-textured Zoysia with beautiful emerald color and excellent wear tolerance',
    descriptionEs: 'Zoysia de textura fina con hermoso color esmeralda y excelente tolerancia al desgaste',
    features: ['Fine Texture', 'Drought Tolerant', 'Wear Resistant', 'Cold Hardy'],
    featuresEs: ['Textura Fina', 'Tolerante a la Sequía', 'Resistente al Desgaste', 'Resistente al Frío'],
    sunRequirement: '6-8 hours',
    waterNeed: 'Low to Moderate',
    traffic: 'High',
    establishment: '3-4 weeks',
    bestFor: ['Golf courses', 'Athletic fields', 'High-traffic areas']
  },
  {
    id: 'zoysia-empire',
    name: 'Zoysia Empire',
    nameEs: 'Zoysia Empire',
    price: 1.05,
    image: grassVarietiesImage,
    description: 'Medium-textured Zoysia with excellent heat and drought tolerance',
    descriptionEs: 'Zoysia de textura media con excelente tolerancia al calor y sequía',
    features: ['Heat Tolerant', 'Drought Resistant', 'Dense Turf', 'Moderate Maintenance'],
    featuresEs: ['Tolerante al Calor', 'Resistente a la Sequía', 'Césped Denso', 'Mantenimiento Moderado'],
    sunRequirement: '6-8 hours',
    waterNeed: 'Low',
    traffic: 'Medium to High',
    establishment: '2-3 weeks',
    bestFor: ['Residential lawns', 'Commercial', 'Water-wise']
  },
  {
    id: 'zoysia-stadium',
    name: 'Zoysia Stadium',
    nameEs: 'Zoysia Stadium',
    price: 1.10,
    image: grassVarietiesImage,
    description: 'Durable Zoysia cultivar ideal for high-traffic areas and sports',
    descriptionEs: 'Cultivar de Zoysia duradero ideal para áreas de alto tráfico y deportes',
    features: ['Traffic Resistant', 'Dense Growth', 'Drought Tolerant', 'Wear Resistant'],
    featuresEs: ['Resistente al Tráfico', 'Crecimiento Denso', 'Tolerante a la Sequía', 'Resistente al Desgaste'],
    sunRequirement: '6-8 hours',
    waterNeed: 'Low to Moderate',
    traffic: 'High',
    establishment: '3-4 weeks',
    bestFor: ['Sports fields', 'Parks', 'High-use areas']
  },
  {
    id: 'zoysia-zeon',
    name: 'Zoysia Zeon',
    nameEs: 'Zoysia Zeon',
    price: 1.15,
    image: grassVarietiesImage,
    description: 'Fine-bladed Zoysia prized for shade tolerance and soft feel',
    descriptionEs: 'Zoysia de hoja fina apreciada por su tolerancia a la sombra y textura suave',
    features: ['Shade Tolerant', 'Fine Texture', 'Drought Resistant', 'Low Thatch'],
    featuresEs: ['Tolerante a la Sombra', 'Textura Fina', 'Resistente a la Sequía', 'Bajo Thatch'],
    sunRequirement: '4-6 hours',
    waterNeed: 'Low to Moderate',
    traffic: 'Medium',
    establishment: '3-4 weeks',
    bestFor: ['Shaded lawns', 'Premium residential', 'Golf']
  },
  {
    id: 'zoysia-palisades',
    name: 'Zoysia Palisades',
    nameEs: 'Zoysia Palisades',
    price: 1.10,
    image: grassVarietiesImage,
    description: 'Coarser-textured Zoysia known for upright growth and durability',
    descriptionEs: 'Zoysia de textura más gruesa conocida por su crecimiento vertical y durabilidad',
    features: ['Upright Growth', 'Drought Tolerant', 'Wear Resistant', 'Dense Turf'],
    featuresEs: ['Crecimiento Vertical', 'Tolerante a la Sequía', 'Resistente al Desgaste', 'Césped Denso'],
    sunRequirement: '6-8 hours',
    waterNeed: 'Low to Moderate',
    traffic: 'High',
    establishment: '3-4 weeks',
    bestFor: ['Front yards', 'Commercial', 'High traffic']
  }
];

const GrassPage = () => {
  const { language } = useLanguage();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGrassProducts = async () => {
      try {
        const { data, error } = await supabase
          .from('products')
          .select(`
            *,
            price_book (*)
          `)
          .eq('categories.slug_en', 'grass')
          .eq('is_active', true)
          .order('sort_order');

        if (error) throw error;
        setProducts(data || []);
      } catch (error) {
        console.error('Error fetching grass products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGrassProducts();
  }, []);

  const getProductName = (product: Product) => {
    return language === 'en' ? product.name_en : product.name_es;
  };

  const getProductDescription = (product: Product) => {
    return language === 'en' ? product.description_en : product.description_es;
  };

  const getProductFeatures = (product: Product) => {
    return language === 'en' ? product.features_en : product.features_es;
  };

  const getProductPrice = (product: Product) => {
    const priceInfo = product.price_book?.[0];
    if (!priceInfo) return '$0';
    const price = priceInfo.sale_price || priceInfo.list_price;
    return `$${price}`;
  };

  const getUnitLabel = (product: Product) => {
    const priceInfo = product.price_book?.[0];
    if (!priceInfo) return '';
    return language === 'en' ? priceInfo.unit_label_en : priceInfo.unit_label_es;
  };

  const seoTitle = language === 'en' 
    ? 'Premium Grass & Sod Delivery Texas | St. Augustine, Zoysia'
    : 'Entrega de Pasto y Césped Premium Texas | St. Augustine, Zoysia';
  
  const seoDescription = language === 'en'
    ? 'Quality grass & sod delivery in Montgomery County TX. St. Augustine, Zoysia, Bermuda varieties. Professional installation & expert consultation available.'
    : 'Entrega de pasto y césped de calidad en el Condado Montgomery TX. Variedades St. Augustine, Zoysia, Bermuda. Instalación profesional y consulta experta disponible.';
  
  const seoKeywords = language === 'en'
    ? 'sod delivery texas, grass installation montgomery county, st augustine grass texas, zoysia sod delivery, bermuda grass texas, lawn installation conroe'
    : 'entrega cesped texas, instalación pasto montgomery county, pasto st augustine texas, entrega zoysia, bermuda grass texas, instalación jardín conroe';

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": seoTitle,
    "description": seoDescription,
    "category": "Landscaping Supplies",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "provider": {
      "@type": "LocalBusiness",
      "name": "The Grass Depot LLC",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "TX",
        "addressLocality": "Montgomery County"
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={language === 'en' ? '/en/grass' : '/es/pasto'}
        structuredData={structuredData}
        language={language}
        breadcrumbs={[
          { name: language === 'en' ? 'Home' : 'Inicio', url: `/${language}` },
          { name: language === 'en' ? 'Products' : 'Productos', url: language === 'en' ? '/en/products' : '/es/productos' },
          { name: language === 'en' ? 'Grass & Sod' : 'Pasto y Césped', url: language === 'en' ? '/en/grass' : '/es/pasto' },
        ]}
      />
      <Header />
      
      <main>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-hero text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent animate-pulse-glow"></div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="animate-fade-in">
            <div className="flex items-center justify-center mb-6">
              <Leaf className="h-12 w-12 mr-4 animate-float" />
              <h1 className="text-4xl md:text-6xl font-bold">
                {language === 'en' ? 'Premium Grass & Sod' : 'Pasto y Césped Premium'}
              </h1>
            </div>
            <p className="text-xl md:text-2xl text-white/90 max-w-4xl mx-auto mb-8 leading-relaxed">
              {language === 'en'
                ? 'Premium grass varieties, Fresh Cut from the Farm, carefully selected for Texas climate conditions. From St. Augustine to Zoysia, we have the perfect sod for your needs.'
                : 'Variedades premium de césped, Recién Cortado de la Granja, cuidadosamente seleccionadas para las condiciones climáticas de Texas. Desde St. Augustine hasta Zoysia, tenemos el césped perfecto para sus necesidades.'
              }
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base">
                <CheckCircle className="h-5 w-5 mr-2" />
                {language === 'en' ? '🌱 Texas Climate Adapted' : '🌱 Adaptado al Clima de Texas'}
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base">
                <CheckCircle className="h-5 w-5 mr-2" />
                {language === 'en' ? 'Professional Sod Installation' : 'Instalación Profesional de Césped'}
              </Badge>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`}>
                <Button variant="hero" size="lg" className="hover-scale">
                  {language === 'en' ? 'Get Quote Now' : 'Obtener Cotización Ahora'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:9363200763">
                <Button variant="outline" size="lg" className="bg-white/95 border-white text-primary font-semibold hover:bg-white hover:text-primary">
                  <Phone className="mr-2 h-5 w-5" />
                  {language === 'en' ? 'Call Expert' : 'Llamar Experto'}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats Section */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { number: '6+', label: language === 'en' ? 'Grass Varieties' : 'Variedades de Césped', icon: Leaf },
              { number: '11+', label: language === 'en' ? 'Grass Varieties' : 'Variedades de Césped', icon: Star },
              { number: '24hr', label: language === 'en' ? 'Fresh Cut Delivery' : 'Entrega Recién Cortado', icon: Clock },
              { number: '100%', label: language === 'en' ? 'Satisfaction Rate' : 'Tasa de Satisfacción', icon: CheckCircle }
            ].map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="text-center animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="h-8 w-8 text-primary" />
                  </div>
                  <div className="text-3xl font-bold text-primary mb-2">{stat.number}</div>
                  <div className="text-muted-foreground font-medium">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Grass Selection Guide */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Choose Your Perfect Grass' : 'Elija su Césped Perfecto'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {language === 'en'
                ? 'Each variety is specially selected for optimal performance in Texas weather conditions. Use our guide to find the perfect match for your landscape needs.'
                : 'Cada variedad está especialmente seleccionada para un rendimiento óptimo en las condiciones climáticas de Texas. Use nuestra guía para encontrar la combinación perfecta para sus necesidades de paisajismo.'
              }
            </p>
          </div>

          <Tabs defaultValue="grid" className="w-full">
            <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto mb-12">
              <TabsTrigger value="grid">
                {language === 'en' ? 'View All' : 'Ver Todos'}
              </TabsTrigger>
              <TabsTrigger value="compare">
                {language === 'en' ? 'Compare' : 'Comparar'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="grid">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {grassVarieties.map((grass, index) => (
                  <Card 
                    key={grass.id} 
                    className="group hover:shadow-glow transition-all duration-500 border-2 hover:border-primary overflow-hidden animate-fade-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={grass.image} 
                        alt={language === 'en' ? grass.name : grass.nameEs}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute top-4 right-4 bg-white rounded-lg px-3 py-1 shadow-lg">
                        <span className="font-bold text-primary">
                          Premium Quality
                        </span>
                      </div>
                      <div className="absolute bottom-4 left-4">
                        <h3 className="text-xl font-bold text-white mb-1">
                          {language === 'en' ? grass.name : grass.nameEs}
                        </h3>
                      </div>
                    </div>
                    
                    <CardContent className="p-6">
                      <p className="text-muted-foreground mb-4 text-sm">
                        {language === 'en' ? grass.description : grass.descriptionEs}
                      </p>
                      
                      {/* Features */}
                      <div className="grid grid-cols-2 gap-2 mb-6">
                        {(language === 'en' ? grass.features : grass.featuresEs).slice(0, 4).map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-primary flex-shrink-0" />
                            <span className="text-xs text-muted-foreground">{feature}</span>
                          </div>
                        ))}
                      </div>

                      {/* Quick specs */}
                      <div className="space-y-2 mb-6 text-xs">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            {language === 'en' ? 'Sun:' : 'Sol:'}
                          </span>
                          <span className="font-medium">{grass.sunRequirement}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            {language === 'en' ? 'Water:' : 'Agua:'}
                          </span>
                          <span className="font-medium">{grass.waterNeed}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            {language === 'en' ? 'Traffic:' : 'Tráfico:'}
                          </span>
                          <span className="font-medium">{grass.traffic}</span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`} className="flex-1">
                          <Button variant="hero" className="w-full group-hover:scale-105 transition-transform">
                            {language === 'en' ? 'Add to Quote' : 'Agregar a Cotización'}
                          </Button>
                        </Link>
                        <Button variant="outline" size="icon" className="hover-scale">
                          <Leaf className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="compare">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-background rounded-lg overflow-hidden shadow-lg">
                  <thead>
                    <tr className="bg-primary text-primary-foreground">
                      <th className="p-4 text-left font-semibold">
                        {language === 'en' ? 'Grass Type' : 'Tipo de Césped'}
                      </th>
                      <th className="p-4 text-center font-semibold">
                        {language === 'en' ? 'Best For' : 'Mejor Para'}
                      </th>
                      <th className="p-4 text-center font-semibold">
                        {language === 'en' ? 'Sun Needs' : 'Necesidades de Sol'}
                      </th>
                      <th className="p-4 text-center font-semibold">
                        {language === 'en' ? 'Water Needs' : 'Necesidades de Agua'}
                      </th>
                      <th className="p-4 text-center font-semibold">
                        {language === 'en' ? 'Traffic Tolerance' : 'Tolerancia al Tráfico'}
                      </th>
                      <th className="p-4 text-center font-semibold">
                        {language === 'en' ? 'Establishment' : 'Establecimiento'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {grassVarieties.map((grass, index) => (
                      <tr key={grass.id} className={`border-b ${index % 2 === 0 ? 'bg-muted/30' : 'bg-background'} hover:bg-primary/5 transition-colors`}>
                        <td className="p-4">
                          <div className="font-semibold text-foreground">
                            {language === 'en' ? grass.name : grass.nameEs}
                          </div>
                        </td>
                        <td className="p-4 text-center font-medium text-foreground">
                          {grass.bestFor.join(', ')}
                        </td>
                        <td className="p-4 text-center text-muted-foreground">
                          {grass.sunRequirement}
                        </td>
                        <td className="p-4 text-center text-muted-foreground">
                          {grass.waterNeed}
                        </td>
                        <td className="p-4 text-center text-muted-foreground">
                          {grass.traffic}
                        </td>
                        <td className="p-4 text-center text-muted-foreground">
                          {grass.establishment}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Installation Guide Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {language === 'en' ? 'Professional Installation & Care Guide' : 'Guía de Instalación y Cuidado Profesional'}
              </h2>
              <p className="text-lg text-muted-foreground">
                {language === 'en'
                  ? 'Get expert guidance for the perfect lawn installation and long-term care'
                  : 'Obtenga orientación experta para la instalación perfecta del césped y cuidado a largo plazo'
                }
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="hover:shadow-glow transition-all duration-300 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <AlertTriangle className="h-6 w-6 text-amber-600" />
                    {language === 'en' ? 'Why Hire a Professional to Install Sod' : 'Por Qué Contratar a un Profesional para Instalar Césped'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold text-foreground mb-2">
                        {language === 'en' ? 'Proper Ground Preparation' : 'Preparación Adecuada del Terreno'}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {language === 'en'
                          ? 'Professionals grade and level the soil so the sod sits evenly and establishes quickly.'
                          : 'Los profesionales nivelen y nivelan el suelo para que el césped se asiente uniformemente y se establezca rápidamente.'
                        }
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-foreground mb-2">
                        {language === 'en' ? 'Correct Installation Techniques' : 'Técnicas de Instalación Correctas'}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {language === 'en'
                          ? 'Sod is laid tightly with staggered seams for a smooth, finished look.'
                          : 'El césped se coloca firmemente con costuras escalonadas para un aspecto suave y terminado.'
                        }
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-foreground mb-2">
                        {language === 'en' ? 'Save Your Back' : 'Protege Tu Espalda'}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {language === 'en'
                          ? 'Laying sod is heavy, physical work. A professional crew handles the lifting, cutting, and hauling so you don\'t have to.'
                          : 'Colocar césped es trabajo físico pesado. Un equipo profesional maneja el levantamiento, corte y transporte para que no tengas que hacerlo.'
                        }
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-foreground mb-2">
                        {language === 'en' ? 'Efficiency & Time Savings' : 'Eficiencia y Ahorro de Tiempo'}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {language === 'en'
                          ? 'A pro team can complete in hours what might take a homeowner several days, reducing the risk of sod drying out.'
                          : 'Un equipo profesional puede completar en horas lo que podría tomarle a un propietario varios días, reduciendo el riesgo de que el césped se seque.'
                        }
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-foreground mb-2">
                        {language === 'en' ? 'Expert Guidance' : 'Orientación Experta'}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {language === 'en'
                          ? 'Professionals can recommend the best sod variety for your yard and provide clear care instructions after installation.'
                          : 'Los profesionales pueden recomendar la mejor variedad de césped para su patio y proporcionar instrucciones claras de cuidado después de la instalación.'
                        }
                      </p>
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <h4 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      {language === 'en' ? 'Common DIY Mistakes' : 'Errores Comunes DIY'}
                    </h4>
                    <div className="space-y-2 text-sm text-red-700">
                      <p>• {language === 'en' ? 'Inadequate soil preparation that prevents sod from establishing properly' : 'Preparación inadecuada del suelo que impide que el césped se establezca correctamente'}</p>
                      <p>• {language === 'en' ? 'Choosing the wrong sod variety for the yard\'s sunlight, soil, or use' : 'Elegir la variedad incorrecta de césped para la luz solar, suelo o uso del patio'}</p>
                      <p>• {language === 'en' ? 'Laying sod incorrectly, leaving gaps, overlaps, or uneven seams' : 'Colocar el césped incorrectamente, dejando huecos, superposiciones o costuras desiguales'}</p>
                      <p>• {language === 'en' ? 'Taking too long to install, allowing sod to dry out and struggle to root' : 'Tomar demasiado tiempo para instalar, permitiendo que el césped se seque y tenga dificultades para enraizar'}</p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`}>
                      <Button variant="hero" className="w-full">
                        <Calendar className="mr-2 h-4 w-4" />
                        {language === 'en' ? 'Get Professional Installation Quote' : 'Obtener Cotización de Instalación Profesional'}
                      </Button>
                    </Link>
                    <a href="tel:9363200763">
                      <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary/10">
                        <Phone className="mr-2 h-4 w-4" />
                        {language === 'en' ? 'Speak with Installation Expert' : 'Hablar con Experto en Instalación'}
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-glow transition-all duration-300">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Calendar className="h-6 w-6 text-primary" />
                    {language === 'en' ? 'Professional Installation' : 'Instalación Profesional'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-6">
                    {language === 'en'
                      ? 'Let our professionals handle your sod installation and take the hard work off your hands.'
                      : 'Deje que nuestros profesionales manejen la instalación de su césped y le quiten el trabajo duro de las manos.'
                    }
                  </p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span>{language === 'en' ? 'Proper ground preparation' : 'Preparación adecuada del terreno'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span>{language === 'en' ? 'Fresh sod delivery & expert installation' : 'Entrega de césped fresco e instalación experta'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span>{language === 'en' ? 'Save your back — we do the heavy lifting' : 'Protege tu espalda — nosotros hacemos el trabajo pesado'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span>{language === 'en' ? 'Fast, efficient service to keep sod fresh' : 'Servicio rápido y eficiente para mantener el césped fresco'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span>{language === 'en' ? 'Expert guidance on watering and care' : 'Asesoramiento experto sobre riego y cuidado'}</span>
                    </div>
                  </div>
                  <Button variant="hero" className="w-full">
                    <Calendar className="mr-2 h-4 w-4" />
                    {language === 'en' ? 'Schedule Installation' : 'Programar Instalación'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Care Tips Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Grass Care Essentials' : 'Elementos Esenciales del Cuidado del Césped'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {language === 'en'
                ? 'Essential tips to keep your new sod healthy and thriving throughout the Texas seasons'
                : 'Consejos esenciales para mantener su nuevo césped saludable y próspero durante las estaciones de Texas'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Droplets,
                title: language === 'en' ? 'Proper Watering' : 'Riego Adecuado',
                tip: language === 'en' 
                  ? 'Water deeply 2-3 times per week rather than daily light watering. Early morning is best.'
                  : 'Riegue profundamente 2-3 veces por semana en lugar de riego ligero diario. Temprano en la mañana es mejor.',
                schedule: language === 'en' 
                  ? 'First 2 weeks: Daily\nWeeks 3-4: Every other day\nEstablished: 2-3x weekly'
                  : 'Primeras 2 semanas: Diario\nSemanas 3-4: Día por medio\nEstablecido: 2-3x semanal'
              },
              {
                icon: Sun,
                title: language === 'en' ? 'Sunlight Requirements' : 'Requisitos de Luz Solar',
                tip: language === 'en'
                  ? 'Most grass types need 6-8 hours of direct sunlight daily for optimal growth.'
                  : 'La mayoría de los tipos de césped necesitan 6-8 horas de luz solar directa diariamente para crecimiento óptimo.',
                schedule: language === 'en' 
                  ? 'St. Augustine: 4-8 hours\nZoysia: 6-8 hours\nBermuda: 8+ hours'
                  : 'St. Augustine: 4-8 horas\nZoysia: 6-8 horas\nBermuda: 8+ horas'
              },
              {
                icon: Thermometer,
                title: language === 'en' ? 'Best Planting Times' : 'Mejores Tiempos de Plantación',
                tip: language === 'en'
                  ? 'Plant in spring (March-May) or fall (September-November) for best establishment results.'
                  : 'Plante en primavera (marzo-mayo) u otoño (septiembre-noviembre) para mejores resultados de establecimiento.',
                schedule: language === 'en' 
                  ? 'Spring: March-May\nFall: Sept-November\nAvoid: Summer extremes'
                  : 'Primavera: Marzo-Mayo\nOtoño: Sept-Noviembre\nEvitar: Extremos de verano'
              },
              {
                icon: Leaf,
                title: language === 'en' ? 'Fertilization Schedule' : 'Horario de Fertilización',
                tip: language === 'en'
                  ? 'Apply slow-release fertilizer 3-4 times per year based on grass type and season.'
                  : 'Aplique fertilizante de liberación lenta 3-4 veces al año basado en tipo de césped y estación.',
                schedule: language === 'en' 
                  ? 'Early Spring\nLate Spring\nSummer\nFall prep'
                  : 'Inicio Primavera\nFin Primavera\nVerano\nPrep Otoño'
              }
            ].map((tip, index) => (
              <Card key={index} className="hover:shadow-glow transition-all duration-300 group">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                    <tip.icon className="h-8 w-8 text-primary group-hover:animate-float" />
                  </div>
                  <CardTitle className="text-xl font-bold text-foreground">
                    {tip.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    {tip.tip}
                  </p>
                  <div className="bg-primary/5 rounded-lg p-3">
                    <h4 className="font-semibold text-foreground text-xs mb-2">
                      {language === 'en' ? 'Schedule:' : 'Horario:'}
                    </h4>
                    <div className="text-xs text-muted-foreground whitespace-pre-line">
                      {tip.schedule}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Care Tips */}
          <div className="mt-16 max-w-4xl mx-auto">
            <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="text-2xl text-center text-foreground">
                  {language === 'en' ? 'Pro Tips for Texas Grass Care' : 'Consejos Profesionales para Cuidado del Césped en Texas'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      {language === 'en' ? 'Summer Care' : 'Cuidado de Verano'}
                    </h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• {language === 'en' ? 'Raise mowing height during hot months' : 'Aumente la altura de corte durante meses calurosos'}</li>
                      <li>• {language === 'en' ? 'Water in early morning to reduce evaporation' : 'Riegue temprano en la mañana para reducir evaporación'}</li>
                      <li>• {language === 'en' ? 'Apply summer fertilizer in June' : 'Aplique fertilizante de verano en junio'}</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      {language === 'en' ? 'Winter Preparation' : 'Preparación de Invierno'}
                    </h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• {language === 'en' ? 'Final fertilization in late October' : 'Fertilización final a finales de octubre'}</li>
                      <li>• {language === 'en' ? 'Reduce watering frequency' : 'Reduzca la frecuencia de riego'}</li>
                      <li>• {language === 'en' ? 'Keep grass slightly longer for cold protection' : 'Mantenga el césped un poco más largo para protección del frío'}</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'en' 
              ? 'Ready for the Perfect Lawn?' 
              : '¿Listo para el Césped Perfecto?'
            }
          </h2>
          <p className="text-lg text-secondary-foreground/80 mb-8 max-w-3xl mx-auto">
            {language === 'en'
              ? 'Contact our grass experts today for personalized recommendations and professional installation services. We focus on delivering quality sod and expert installation for every project.'
              : 'Contacte a nuestros expertos en césped hoy para recomendaciones personalizadas y servicios de instalación profesional. Nos enfocamos en entregar césped de calidad e instalación experta para cada proyecto.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`}>
              <Button variant="hero" size="lg" className="hover-scale">
                {language === 'en' ? 'Get Free Quote' : 'Obtener Cotización Gratis'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="tel:9363200763">
              <Button variant="outline" size="lg" className="bg-white/95 border-white text-primary font-semibold hover:bg-white hover:text-primary">
                <Phone className="mr-2 h-5 w-5" />
                {language === 'en' ? 'Call Expert' : 'Llamar Experto'}
              </Button>
            </a>
          </div>
        </div>
      </section>
      <ExpandedFAQSection faqs={grassFaqs} language={language as 'en' | 'es'} />
      <RelatedMaterialsLinks language={language as 'en' | 'es'} exclude={['grass']} />
      </main>
      
      <Footer />
      <MobileCTA />
    </div>
  );
};

export default GrassPage;